import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatMenuTrigger } from '@angular/material/menu';
import { Subject, Subscription, fromEvent, interval, takeUntil } from 'rxjs';
import {
    DisplayStatus,
    Subsystem,
    SystemStatusService,
} from '../../../core/services/system-status.service';
import { AnnouncementGateService } from '../../../core/services/announcement-gate.service';

/**
 * The system-status dot in the clinical header.
 *
 * `tasks/system-status.md` §11.
 *
 * ── FOUR THINGS HERE ARE REQUIREMENTS, NOT POLISH ────────────────────────────────────
 *
 * 1. **It is a button.** The app ships under Capacitor on iPads and phones, where hover does not
 *    exist. A hover-only panel would make the dot decorative on exactly the devices used at the
 *    bedside. Hover is added on top for pointer devices.
 *
 * 2. **Colour is never the only signal.** Green/amber is the pairing that fails for the commonest
 *    form of colour blindness, so every state is drawn as a distinct SHAPE (circle, triangle,
 *    square, diamond, ring), the panel spells the state out in words, and the trigger carries an
 *    `aria-label` naming the worst state.
 *
 *    The shapes are drawn in CSS rather than set as text glyphs (● ▲ ■ ◆ ○), which is what they
 *    were first. A glyph's optical centre depends on the font's baseline metrics, not on its box,
 *    so the pulse halo — centred on the box — visibly sat off-centre from the mark it was meant to
 *    radiate from. A CSS shape and its halo share one box and cannot drift apart.
 *
 * 3. **Grey when we could not ask — and a way back.** The dot polls, so a session that has been
 *    open for hours still holds a current answer, and a grey dot recovers by itself once the API is
 *    reachable again. Without the poll, `unknown` was a dead end: nothing re-fetched, so every idle
 *    session went grey after five minutes and stayed there until a reload.
 *
 *    The timer lives here rather than in the service so that it runs exactly while the dot is on
 *    screen, stops at logout, and costs nothing in a background tab.
 *
 * 4. **A silent colour change gets missed.** A subsystem degrading while somebody is working
 *    produces one transient snackbar — not a dialog, not blocking — and only for a TRANSITION, so
 *    reloading during an ongoing incident does not re-announce it.
 */
@Component({
    selector: 'app-system-status-dot',
    templateUrl: './system-status-dot.component.html',
    styleUrls: ['./system-status-dot.component.scss'],
})
export class SystemStatusDotComponent implements OnInit, OnDestroy {

    overall$ = this.systemStatus.overall$;
    subsystems$ = this.systemStatus.subsystems$;

    /**
     * "Updated 3 minutes ago", recomputed on the same tick that re-evaluates staleness.
     *
     * It was a hardcoded "just now" — which would have said "just now" over a snapshot half an hour
     * old, on a panel whose whole job is telling the truth about how current it is.
     */
    checkedAtLabel = 'just now';
    private checkedAt: Date | null = null;

    /** One snackbar per subsystem per session — enough to look up, not enough to nag. */
    private announced = new Set<string>();
    private destroy$ = new Subject<void>();

    /**
     * The tick. Short, because it is only a decision — most ticks do nothing but re-render the
     * "updated N minutes ago" line.
     */
    private static readonly TICK_MS = 20 * 1000;

    /**
     * How stale a HEALTHY answer is allowed to get before refetching.
     *
     * Comfortably inside the service's five-minute staleness threshold, so a working session never
     * drifts into grey; and comfortably outside the server's ~30s cache, so most polls are served
     * from memory rather than the database.
     */
    private static readonly POLL_MS = 2 * 60 * 1000;

    /**
     * How often to retry while the dot is grey.
     *
     * Faster than the healthy poll, because this is the one state the user actively wants to leave
     * — but not so fast that a genuinely offline client hammers a dead endpoint.
     */
    private static readonly RETRY_MS = 20 * 1000;

    /**
     * How long the pointer may be off both the dot and the panel before a hover-opened panel closes.
     *
     * Long enough to cross the corner between the two without the panel vanishing under the
     * cursor; short enough that it still reads as "closes when I move away".
     */
    private static readonly HOVER_CLOSE_DELAY_MS = 300;

    /** A few pixels of slack around the 10px dot, so grazing its edge does not count as leaving. */
    private static readonly HOVER_SLACK_PX = 6;

    /** Live only while the panel was opened by hover; see `openOnHover`. */
    private hoverTracking: Subscription | null = null;
    private hoverCloseTimer: ReturnType<typeof setTimeout> | null = null;

    private onVisibilityChange = () => {
        // Coming back to the tab is the single best moment to refetch: the answer is most likely to
        // be stale and the user is most likely to be looking at it.
        if (!document.hidden) this.systemStatus.load().subscribe();
    };

    constructor(
        private systemStatus: SystemStatusService,
        private snackBar: MatSnackBar,
        private gate: AnnouncementGateService,
        private zone: NgZone,
    ) { }

    ngOnInit(): void {
        // Re-evaluated here rather than on an interval inside the service: a service polling in the
        // background would hold a timer for the life of the app to change a colour nobody may be
        // looking at. The component only ticks while it is on screen.
        this.systemStatus.checkedAt$
            .pipe(takeUntil(this.destroy$))
            .subscribe((iso) => {
                this.checkedAt = iso ? new Date(iso) : null;
                this.updateCheckedAtLabel();
            });

        interval(SystemStatusDotComponent.TICK_MS)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.tick());

        document.addEventListener('visibilitychange', this.onVisibilityChange);

        this.systemStatus.justDegraded$
            .pipe(takeUntil(this.destroy$))
            .subscribe((subsystem) => this.announce(subsystem));
    }

    ngOnDestroy(): void {
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.stopHoverTracking();
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * One tick: re-evaluate staleness, refresh the label, and refetch if it is time.
     *
     * A hidden tab does nothing at all. It cannot be looked at, and a laptop asleep in a bag should
     * not be retrying an endpoint every twenty seconds — `visibilitychange` refetches the moment it
     * comes back, which is both cheaper and fresher than having polled throughout.
     */
    private tick(): void {
        this.systemStatus.refreshFreshness();
        this.updateCheckedAtLabel();

        if (document.hidden) return;

        const due = this.systemStatus.displayStatus === 'unknown'
            ? SystemStatusDotComponent.RETRY_MS
            : SystemStatusDotComponent.POLL_MS;

        if (this.systemStatus.ageOfLastSuccess >= due) {
            this.systemStatus.load().subscribe();
        }
    }

    /**
     * The status to render for one row.
     *
     * When the overall dot is `unknown` every row is `unknown` too. Showing eleven green "NORMAL"
     * rows under a "Cannot reach MedicAI" header claimed to know exactly what the header had just
     * said we could not find out — the panel contradicted itself, and the reassuring half was the
     * one that was wrong.
     */
    rowStatus(subsystem: Subsystem, overall: DisplayStatus): DisplayStatus {
        return overall === 'unknown' ? 'unknown' : subsystem.status;
    }

    /**
     * One transient snackbar when a subsystem degrades mid-session.
     *
     * Gated by `AnnouncementGateService` so it never fires during a recording — the same rule the
     * announcement dialog obeys, reused rather than re-implemented. A snackbar is far less
     * intrusive than a dialog, but "less intrusive" is not "acceptable mid-consultation".
     */
    private announce(subsystem: Subsystem | null): void {
        if (!subsystem) return;
        if (this.announced.has(subsystem.key)) return;
        if (!this.gate.canInterrupt()) return;

        this.announced.add(subsystem.key);
        this.snackBar.open(
            `${subsystem.label}: ${this.stateLabel(subsystem.status)}${subsystem.message ? ' — ' + subsystem.message : ''}`,
            'OK',
            { duration: 8000, horizontalPosition: 'right', verticalPosition: 'top' },
        );
    }

    private updateCheckedAtLabel(): void {
        if (!this.checkedAt) { this.checkedAtLabel = 'never'; return; }
        const minutes = Math.floor((Date.now() - this.checkedAt.getTime()) / 60000);
        if (minutes < 1) { this.checkedAtLabel = 'just now'; return; }
        if (minutes === 1) { this.checkedAtLabel = '1 minute ago'; return; }
        if (minutes < 60) { this.checkedAtLabel = `${minutes} minutes ago`; return; }
        const hours = Math.floor(minutes / 60);
        this.checkedAtLabel = hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    }

    /**
     * Opens the panel on hover, for pointer devices only. Tap already opens it via the trigger.
     *
     * A panel opened by hover also CLOSES by hover: once the pointer is off both the dot and the
     * panel for `HOVER_CLOSE_DELAY_MS`, it goes away. A panel opened by tap or keyboard is left
     * alone — it was asked for deliberately and is dismissed the usual way.
     *
     * ── WHY THE POINTER IS TRACKED ON THE DOCUMENT, NOT WITH `mouseleave` ────────────────
     *
     * The menu keeps Material's transparent backdrop, because in this version of Material the
     * backdrop click is the only thing that closes a menu from outside — remove it and a tapped
     * panel could no longer be dismissed by tapping elsewhere. But that backdrop lands on top of
     * the trigger the instant the menu opens, so the button fires `mouseleave` immediately and
     * never hears about the pointer again. Watching `mousemove` on the document and testing the
     * pointer against the button's box and the panel's element works whatever is stacked on top.
     */
    openOnHover(trigger: MatMenuTrigger, triggerEl: HTMLElement): void {
        if (!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches)) {
            return;
        }
        if (trigger.menuOpen) {
            // Back on the dot before the grace period ran out: stay open.
            this.cancelHoverClose();
            return;
        }
        trigger.openMenu();
        this.trackPointerWhileHoverOpen(trigger, triggerEl);
    }

    private trackPointerWhileHoverOpen(trigger: MatMenuTrigger, triggerEl: HTMLElement): void {
        this.stopHoverTracking();

        // Outside the zone: a mousemove handler that ran change detection on every pixel of travel
        // would be the most expensive thing in the header. Only the eventual close re-enters it.
        this.zone.runOutsideAngular(() => {
            const tracking = new Subscription();

            tracking.add(fromEvent<MouseEvent>(document, 'mousemove').subscribe((event) => {
                if (this.isOverTrigger(event, triggerEl) || this.isOverPanel(event)) {
                    this.cancelHoverClose();
                } else {
                    this.scheduleHoverClose(trigger);
                }
            }));

            // However it closes — backdrop click, Escape, our own timer — stop watching.
            tracking.add(trigger.menuClosed.subscribe(() => this.stopHoverTracking()));

            this.hoverTracking = tracking;
        });
    }

    private stopHoverTracking(): void {
        this.cancelHoverClose();
        this.hoverTracking?.unsubscribe();
        this.hoverTracking = null;
    }

    private scheduleHoverClose(trigger: MatMenuTrigger): void {
        if (this.hoverCloseTimer !== null) return;
        this.hoverCloseTimer = setTimeout(() => {
            this.hoverCloseTimer = null;
            this.zone.run(() => trigger.closeMenu());
        }, SystemStatusDotComponent.HOVER_CLOSE_DELAY_MS);
    }

    private cancelHoverClose(): void {
        if (this.hoverCloseTimer === null) return;
        clearTimeout(this.hoverCloseTimer);
        this.hoverCloseTimer = null;
    }

    private isOverTrigger(event: MouseEvent, triggerEl: HTMLElement): boolean {
        const slack = SystemStatusDotComponent.HOVER_SLACK_PX;
        const r = triggerEl.getBoundingClientRect();
        return event.clientX >= r.left - slack && event.clientX <= r.right + slack
            && event.clientY >= r.top - slack && event.clientY <= r.bottom + slack;
    }

    /** The panel renders in a CDK overlay, not under this component, so it is found by its class. */
    private isOverPanel(event: MouseEvent): boolean {
        const target = event.target as Element | null;
        return !!target?.closest?.('.system-status-menu');
    }

    stateLabel(status: DisplayStatus): string {
        switch (status) {
            case 'down': return 'Not working';
            case 'degraded': return 'Degraded';
            case 'maintenance': return 'Maintenance';
            case 'unknown': return 'Unknown';
            default: return 'Normal';
        }
    }

    headline(status: DisplayStatus): string {
        switch (status) {
            case 'down': return 'Some services are not working';
            case 'degraded': return 'Some services are degraded';
            case 'maintenance': return 'Planned maintenance in progress';
            case 'unknown': return 'Cannot reach MedicAI';
            default: return 'All services operational';
        }
    }

    tooltip(status: DisplayStatus): string {
        return `System status: ${this.stateLabel(status).toLowerCase()}`;
    }

    ariaLabel(status: DisplayStatus): string {
        return `${this.headline(status)}. Activate to see each service.`;
    }
}
